import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getTmsApiUrl } from '../utils/apiUrls';

const BASE_URL = getTmsApiUrl();

// ── Helpers ──────────────────────────────────────────────────────────────────

const DASH = <span style={{ color: '#9ca3af' }}>—</span>;

function fmt(val) {
  if (val === null || val === undefined || val === '') return DASH;
  return String(val);
}

function fmtDate(val) {
  if (!val) return DASH;
  try {
    // SkySlope dates come back as ISO strings
    const s = String(val);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}/${m[3]}/${m[1]}`;
    const d = new Date(val);
    if (!isNaN(d.getTime()))
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  } catch { /* fall through */ }
  return String(val);
}

function fmtMoney(val) {
  if (val === null || val === undefined || val === '') return DASH;
  const n = parseFloat(val);
  if (isNaN(n)) return DASH;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtBool(val) {
  if (val === null || val === undefined) return DASH;
  return val ? 'Yes' : 'No';
}

// Canonical helpers matching inbound_tms_sync_service field names
function contactName(c) {
  if (!c) return null;
  const co = c.companyName || c.company;
  const full = [c.firstName, c.lastName].filter(Boolean).join(' ');
  return co || full || null;
}

function contactDisplayName(c) {
  if (!c) return null;
  const full = [c.firstName, c.lastName].filter(Boolean).join(' ');
  const co = c.companyName || c.company;
  if (full && co) return `${full} (${co})`;
  return full || co || null;
}

// Build address from nested property object (camelCase from SkySlope)
function buildAddressFromProp(prop) {
  if (!prop) return null;
  const street = [prop.streetNumber, prop.streetAddress].filter(Boolean).join(' ');
  const parts = [street, prop.city, prop.state, prop.zip].filter(Boolean);
  return parts.join(', ') || null;
}

// Build a map of documentId → checklistItemName from sale.checklist activities.
//
// Per the SkySlope swagger, ChecklistActivity has TWO doc fields:
//   checklistDocs: ChecklistDocument[] — full objects with { id, documentServiceKey, fileName, … }
//   docs:          string[]            — raw document ID/GUID strings
//
// After a listing-to-sale conversion SkySlope may place listing-phase items
// inside checklistSections[].activities rather than the flat activities array.
// We read from both to ensure all items are indexed.
//
// We index by every possible identifier from both arrays so that lookups against
// the standalone /documents endpoint (which returns the same ChecklistDocument shape)
// succeed regardless of which field SkySlope happens to populate.
function buildDocAssignmentMap(deal) {
  const map = {};
  const flatActivities = (deal?.checklist?.activities) || [];
  const sectionActivities = (deal?.checklist?.checklistSections || []).flatMap(s => s.activities || []);
  const activities = [...flatActivities, ...sectionActivities];
  activities.forEach(act => {
    const name = act.activityName || 'Unnamed';

    // checklistDocs: full ChecklistDocument objects
    (act.checklistDocs || []).forEach(doc => {
      [doc.id, doc.documentId, doc.documentGuid, doc.documentServiceKey]
        .filter(x => x != null && x !== '')
        .forEach(id => { map[String(id)] = name; });
    });

    // docs: plain string IDs — index each one directly
    (act.docs || []).forEach(id => {
      if (id && typeof id === 'string') map[id] = name;
    });
  });
  return map;
}

// ── UI primitives ─────────────────────────────────────────────────────────────

function FieldRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: '8px', padding: '5px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ minWidth: '220px', color: '#6b7280', fontSize: '13px', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#111827' }}>{value ?? DASH}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        fontSize: '13px', fontWeight: '600', color: '#374151',
        marginBottom: '8px', borderBottom: '2px solid #e5e7eb', paddingBottom: '4px',
      }}>{title}</div>
      {children}
    </div>
  );
}

// ── Contacts tab ──────────────────────────────────────────────────────────────

function ContactBlock({ title, contacts, extra }) {
  // contacts is an array; extra is an array of [label, value] pairs
  if (!contacts || contacts.length === 0) return null;
  return (
    <Section title={title}>
      {contacts.map((c, i) => (
        <div key={i} style={contacts.length > 1 ? {
          marginBottom: '12px', paddingLeft: '8px', borderLeft: '3px solid #e5e7eb',
        } : {}}>
          {contacts.length > 1 && (
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
              {contactDisplayName(c) || `Contact ${i + 1}`}
            </div>
          )}
          {[
            ['Entity?', fmtBool(c.isEntity || c.isTrustOrCompany)],
            ['First Name', fmt(c.firstName)],
            ['Last Name', fmt(c.lastName)],
            ['Company', fmt(c.companyName || c.company)],
            ['Email', fmt(c.email)],
            ['Phone', fmt(c.phoneNumber || c.phone)],
            ['Alt Phone', fmt(c.alternatePhoneNumber || c.alternatePhone)],
            ['Fax', fmt(c.fax)],
            ['Street #', fmt(c.streetNumber)],
            ['Street Name', fmt(c.streetAddress || c.streetName)],
            ['City', fmt(c.city)],
            ['State', fmt(c.state)],
            ['Zip', fmt(c.zipCode || c.zip)],
            ['Notes', fmt(c.notes)],
          ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
        </div>
      ))}
      {extra && extra.map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
    </Section>
  );
}

function ContactsTab({ deal, dealType }) {
  if (!deal) return null;

  if (dealType === 'listing') {
    const sellers = deal.sellers || [];
    return (
      <div>
        <ContactBlock title="Seller(s)" contacts={sellers} />
        {sellers.length === 0 && <p style={{ color: '#9ca3af', fontSize: '13px' }}>No contacts found.</p>}
      </div>
    );
  }

  // Sale contacts — fields confirmed from inbound_tms_sync_service.py
  const buyers = deal.buyers || [];
  const sellers = deal.sellers || [];
  const title = deal.titleContact ? [deal.titleContact] : [];
  const escrow = deal.escrowContact ? [deal.escrowContact] : [];
  const lender = deal.lenderContact ? [deal.lenderContact] : [];
  const osa = deal.otherSideAgentContact ? [deal.otherSideAgentContact] : [];
  const atty = deal.attorneyContact ? [deal.attorneyContact] : [];

  // TCs may be embedded or not — SkySlope stores them separately but some DTO shapes include them
  const tcs = deal.transactionCoordinators || [];

  const hasAny = buyers.length + sellers.length + title.length + escrow.length +
    lender.length + osa.length + atty.length + tcs.length > 0;

  if (!hasAny) return <p style={{ color: '#9ca3af', fontSize: '13px' }}>No contacts found in SkySlope.</p>;

  return (
    <div>
      <ContactBlock title="Seller(s)" contacts={sellers} />
      <ContactBlock title="Buyer(s)" contacts={buyers} />

      {(title.length > 0 || escrow.length > 0) && (
        <Section title="Title / Escrow / Attorney">
          {[...title.map(c => ({ ...c, _role: 'Title' })), ...escrow.map(c => ({ ...c, _role: 'Escrow' })), ...atty.map(c => ({ ...c, _role: 'Attorney' }))].map((c, i) => (
            <div key={i} style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>{c._role}</div>
              {[
                ['First Name', fmt(c.firstName)],
                ['Last Name', fmt(c.lastName)],
                ['Company', fmt(c.companyName || c.company)],
                ['Email', fmt(c.email)],
                ['Phone', fmt(c.phoneNumber || c.phone)],
                ['Street #', fmt(c.streetNumber)],
                ['Street Name', fmt(c.streetAddress)],
                ['City', fmt(c.city)],
                ['State', fmt(c.state)],
                ['Zip', fmt(c.zipCode || c.zip)],
                ['Notes', fmt(c.notes)],
              ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
            </div>
          ))}
        </Section>
      )}

      {osa.length > 0 && (
        <ContactBlock
          title="Agent on Other Side"
          contacts={osa}
          extra={[
            ['Brokerage', fmt(osa[0]?.coBrokerCompany || osa[0]?.cobrokerCompany || osa[0]?.company)],
            ['Broker Tax ID', fmt(osa[0]?.brokerTaxId)],
          ]}
        />
      )}

      {lender.length > 0 && (
        <Section title="Lender">
          {[
            ['First Name', fmt(lender[0].firstName)],
            ['Last Name', fmt(lender[0].lastName)],
            ['Company', fmt(lender[0].companyName || lender[0].company)],
            ['Email', fmt(lender[0].email)],
            ['Phone', fmt(lender[0].phoneNumber || lender[0].phone)],
            ['Loan Type', fmt(lender[0].loanType || lender[0].typeOfLoan)],
            ['Loan Amount', fmtMoney(lender[0].loanAmount)],
            ['City', fmt(lender[0].city)],
            ['State', fmt(lender[0].state)],
            ['Notes', fmt(lender[0].notes)],
          ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
        </Section>
      )}

      {tcs.length > 0 && (
        <Section title="Transaction Coordinator">
          {[
            ['First Name', fmt(tcs[0].firstName)],
            ['Last Name', fmt(tcs[0].lastName)],
            ['Company', fmt(tcs[0].companyName || tcs[0].company)],
            ['Email', fmt(tcs[0].email)],
            ['Phone', fmt(tcs[0].phoneNumber || tcs[0].phone)],
            ['Has Portal Access', fmtBool(tcs[0].hasAccess)],
          ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
        </Section>
      )}
    </div>
  );
}

// ── Summary tab ───────────────────────────────────────────────────────────────

function SummaryTab({ deal, dealType }) {
  if (!deal) return null;
  const prop = deal.property || {};

  const saleFields = [
    ['Agent', fmt(deal.agentName)],
    ['Office', fmt(deal.officeName || deal.office)],
    ['MLS #', fmt(deal.mlsNumber)],
    ['Checklist Type', fmt(deal.checklistTypeName || deal.checklistType)],
    ['Street #', fmt(prop.streetNumber)],
    ['Direction', fmt(prop.direction)],
    ['Street Name', fmt(prop.streetAddress)],
    ['Unit #', fmt(prop.unit || prop.unitNumber)],
    ['City', fmt(prop.city)],
    ['State', fmt(prop.state)],
    ['Zip Code', fmt(prop.zip)],
    ['County', fmt(prop.county)],
    ['Type (Representation)', fmt(deal.dealType || deal.checklistType)],
    ['Sale Price', fmtMoney(deal.salePrice)],
    ['Year Built', fmt(prop.yearBuilt)],
    ['Source', fmt(deal.source || deal.otherSource)],
    ['Escrow Number', fmt(deal.escrowNumber)],
    ['Office Lead', fmtBool(deal.isOfficeLead)],
    ['File ID / GUID', fmt(deal.saleGuid || deal.guid)],
    ['APN', fmt(prop.apn || prop.assessorParcelNumber)],
    ['Acceptance Date', fmtDate(deal.contractAcceptanceDate)],
    ['Closing Date', fmtDate(deal.escrowClosingDate || deal.actualClosingDate)],
  ];

  const listingFields = [
    ['Agent', fmt(deal.agentName)],
    ['Office', fmt(deal.officeName || deal.office)],
    ['MLS #', fmt(deal.mlsNumber)],
    ['Checklist Type', fmt(deal.checklistTypeName || deal.checklistType)],
    ['Street #', fmt(prop.streetNumber)],
    ['Direction', fmt(prop.direction)],
    ['Street Name', fmt(prop.streetAddress)],
    ['Unit #', fmt(prop.unit || prop.unitNumber)],
    ['City', fmt(prop.city)],
    ['State', fmt(prop.state)],
    ['Zip Code', fmt(prop.zip)],
    ['County', fmt(prop.county)],
    ['Listing Price', fmtMoney(deal.listingPrice)],
    ['Year Built', fmt(prop.yearBuilt)],
    ['APN', fmt(prop.apn || prop.assessorParcelNumber)],
    ['File ID / GUID', fmt(deal.listingGuid || deal.guid)],
    ['Listing Date', fmtDate(deal.listingDate)],
    ['Expiration Date', fmtDate(deal.expirationDate)],
    ['Status', fmt(deal.status)],
  ];

  return (
    <Section title="Transaction Details">
      {(dealType === 'listing' ? listingFields : saleFields).map(([lbl, val]) => (
        <FieldRow key={lbl} label={lbl} value={val} />
      ))}
    </Section>
  );
}

// ── Commission tab ────────────────────────────────────────────────────────────

function CommissionTab({ deal }) {
  if (!deal) return null;
  // Fields confirmed from inbound_tms_sync_service.py _map_sale_to_fields
  const comm = deal.commission || {};
  const emd = deal.earnestMoneyDeposit || {};
  const ref = comm.referral || deal.referral || {};

  return (
    <div>
      <Section title="Commission Info">
        {[
          ['Sale Price', fmtMoney(deal.salePrice)],
          ['Personal Deal', fmtBool(comm.isPersonalDeal)],
          ['Listing Commission %', fmt(comm.listingCommissionPercent)],
          ['Listing Commission $', fmtMoney(comm.listingCommissionAmount)],
          ['Office Gross Commission', fmtMoney(comm.officeGrossCommission)],
          ['Sale Commission %', fmt(comm.saleCommissionPercent)],
          ['Sale Commission $', fmtMoney(comm.salesCommissionAmount)],
          ['Admin / Brokerage Comp', fmtMoney(comm.adminBrokerageComp)],
          ['Other Deductions', fmtMoney(comm.otherDeductions)],
        ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
      </Section>

      <Section title="Deposits">
        {[
          ['Brokerage Holds Earnest Money?', fmtBool(emd.willBrokerageHoldEarnestMoney)],
          ['Deposit Amount', fmtMoney(emd.depositAmount)],
          ['Date of Check', fmtDate(emd.dateOfCheck || emd.depositDueDate)],
          ['Date Posted to Log Book', fmtDate(emd.datePostedToLogBook)],
        ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
      </Section>

      <Section title="Referral Details">
        {[
          ['Referral Type', fmt(ref.referralType || ref.type)],
          ['Referral Agent', fmt(ref.referralAgent || ref.agent)],
          ['Referral Brokerage', fmt(ref.referralBrokerageName || ref.brokerageName)],
          ['Referral Amount $', fmtMoney(ref.referralAmountDollar || ref.amount)],
          ['Referral Amount %', fmt(ref.referralAmountPercent || ref.percent)],
        ].map(([lbl, val]) => <FieldRow key={lbl} label={lbl} value={val} />)}
      </Section>

      <Section title="Commission Instructions">
        <FieldRow
          label="Additional Info"
          value={fmt(
            comm.commissionBreakdownDetails
            || comm.additionalCommissionBreakdown
            || comm.instructions
            || comm.notes
          )}
        />
      </Section>
    </div>
  );
}

// ── Checklist tab ─────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  Required: '#ef4444',
  'If Applicable': '#6b7280',
  'In Review': '#f59e0b',
  Optional: '#6b7280',
  Received: '#16a34a',
};

function ChecklistBadge({ status }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '9999px',
      background: color + '18', color, fontSize: '11px', fontWeight: '600',
      border: `1px solid ${color}40`,
    }}>
      {status}
    </span>
  );
}

function ChecklistActivitiesTable({ activities, emptyMessage }) {
  if (!activities || activities.length === 0) {
    return <p style={{ color: '#9ca3af', fontSize: '13px' }}>{emptyMessage || 'No checklist items found.'}</p>;
  }
  const grouped = {};
  const groupOrder = [];
  activities.forEach((act) => {
    const group = act.checklistGroup || act.group || 'Checklist Items';
    if (!grouped[group]) { grouped[group] = []; groupOrder.push(group); }
    grouped[group].push(act);
  });
  return (
    <div>
      {groupOrder.map(group => (
        <div key={group} style={{ marginBottom: '24px' }}>
          <div style={{
            fontSize: '13px', fontWeight: '700', color: '#1d4ed8', marginBottom: '8px',
            padding: '6px 10px', background: '#eff6ff', borderRadius: '4px',
          }}>{group}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['#', 'Item', 'Requirement', 'Docs'].map((h, i) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: '600',
                    borderBottom: '1px solid #e5e7eb',
                    width: i === 0 ? '36px' : i === 3 ? '60px' : undefined,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped[group].map((act, idx) => {
                const docCount = Math.max((act.checklistDocs || []).length, (act.docs || []).length);
                const reqStatus = act.status || (act.isRequired ? 'Required' : 'If Applicable');
                return (
                  <tr key={act.activityId || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '7px 8px', color: '#9ca3af' }}>{act.order ?? idx + 1}</td>
                    <td style={{ padding: '7px 8px', color: '#111827' }}>{act.activityName || '—'}</td>
                    <td style={{ padding: '7px 8px' }}><ChecklistBadge status={reqStatus} /></td>
                    <td style={{
                      padding: '7px 8px', textAlign: 'center',
                      color: docCount > 0 ? '#16a34a' : '#9ca3af', fontWeight: docCount > 0 ? '600' : '400',
                    }}>{docCount > 0 ? `✓ ${docCount}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ChecklistTab({ activities, listingActivities }) {
  const hasListing = listingActivities && listingActivities.length > 0;
  if ((!activities || activities.length === 0) && !hasListing) {
    return <p style={{ color: '#9ca3af', fontSize: '13px' }}>No checklist items found.</p>;
  }

  return (
    <div>
      {hasListing && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '12px',
            padding: '8px 12px', background: '#fef3c7', borderRadius: '6px',
            borderLeft: '3px solid #d97706',
          }}>Listing Documentation (from linked listing)</div>
          <ChecklistActivitiesTable activities={listingActivities} />
        </div>
      )}
      {activities && activities.length > 0 && (
        <div>
          {hasListing && (
            <div style={{
              fontSize: '14px', fontWeight: '700', color: '#1e40af', marginBottom: '12px',
              padding: '8px 12px', background: '#eff6ff', borderRadius: '6px',
              borderLeft: '3px solid #3b82f6',
            }}>Sale Checklist</div>
          )}
          <ChecklistActivitiesTable activities={activities} />
        </div>
      )}
    </div>
  );
}

// ── Documents tab ─────────────────────────────────────────────────────────────

function DocumentsTab({ documents, docAssignmentMap }) {
  if (!documents || documents.length === 0) {
    return <p style={{ color: '#9ca3af', fontSize: '13px' }}>No documents found.</p>;
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr style={{ background: '#f9fafb' }}>
          {['Document Name', 'Assigned To (Checklist Item)', 'Date Uploaded'].map((h, i) => (
            <th key={h} style={{
              textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: '600',
              borderBottom: '1px solid #e5e7eb', width: i === 2 ? '150px' : undefined,
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {documents.map((doc, i) => {
          // Try every possible ID field — the standalone-docs endpoint and the
          // checklist-embedded docs may use different fields for the same document.
          const allIds = [doc.id, doc.documentId, doc.documentGuid, doc.documentServiceKey]
            .filter(x => x != null && x !== '')
            .map(String);
          const assigned = allIds.map(id => docAssignmentMap[id]).find(Boolean) || null;
          const docId = allIds[0] || String(i);
          return (
            <tr key={docId || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '8px 10px', color: '#111827' }}>{doc.fileName || doc.filename || '—'}</td>
              <td style={{ padding: '8px 10px', color: assigned ? '#111827' : '#9ca3af' }}>
                {assigned || 'Not assigned'}
              </td>
              <td style={{ padding: '8px 10px', color: '#6b7280' }}>
                {fmtDate(doc.uploadDate || doc.createdAt || doc.modifiedDate)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Deal Detail ───────────────────────────────────────────────────────────────

const ALL_TABS = ['Summary', 'Contacts', 'Commission', 'Checklist', 'Documents'];

function DealDetail({ dealData, dealType, documents }) {
  const [activeTab, setActiveTab] = useState('Summary');

  // The backend returns { sale: {...}, documents: [...] } or { listing: {...}, documents: [...] }
  const deal = dealType === 'listing'
    ? (dealData.listing || dealData)
    : (dealData.sale || dealData);

  const prop = deal.property || {};
  const activities = [
    ...(deal.checklist?.activities || []),
    ...(deal.checklist?.checklistSections || []).flatMap(s => s.activities || []),
  ];
  // Listing-phase checklist items (present when sale was converted from a listing).
  // SkySlope keeps these on the listing file rather than the sale.
  const listingActivities = dealData.listing_checklist_activities || [];
  const docAssignmentMap = useMemo(() => buildDocAssignmentMap(deal), [deal]);

  // Visible tabs — listings have no Commission tab
  const tabs = ALL_TABS.filter(t => dealType === 'listing' ? t !== 'Commission' : true);

  // Address from nested property object (canonical field names)
  const street = [prop.streetNumber, prop.streetAddress].filter(Boolean).join(' ');
  const address = [street, prop.city, prop.state, prop.zip].filter(Boolean).join(', ') || '—';

  // Buyer / seller display names for header
  const buyerNames = (deal.buyers || []).map(b =>
    b.companyName || b.company || [b.firstName, b.lastName].filter(Boolean).join(' ')
  ).filter(Boolean);
  const sellerNames = (deal.sellers || []).map(s =>
    s.companyName || s.company || [s.firstName, s.lastName].filter(Boolean).join(' ')
  ).filter(Boolean);

  // Header fields
  const headerFields = dealType === 'listing' ? [
    { label: 'Address', value: address },
    { label: 'Agent', value: fmt(deal.agentName) },
    { label: 'Listing Price', value: fmtMoney(deal.listingPrice) },
    { label: 'Listing Date', value: fmtDate(deal.listingDate) },
    { label: 'Expiration Date', value: fmtDate(deal.expirationDate) },
    { label: 'Seller(s)', value: sellerNames.length ? sellerNames.join(', ') : DASH },
    { label: 'MLS #', value: fmt(deal.mlsNumber) },
    { label: 'Year Built', value: fmt(prop.yearBuilt) },
    { label: 'Checklist Type', value: fmt(deal.checklistTypeName || deal.checklistType) },
    { label: 'Office', value: fmt(deal.officeName || deal.office) },
    { label: 'Status', value: fmt(deal.status) },
    { label: 'Transaction Email', value: fmt(deal.transactionEmail) },
  ] : [
    { label: 'Address', value: address },
    { label: 'Agent', value: fmt(deal.agentName) },
    { label: 'Close of Escrow', value: fmtDate(deal.escrowClosingDate || deal.actualClosingDate) },
    { label: 'Sale Price', value: fmtMoney(deal.salePrice) },
    { label: 'Buyer(s)', value: buyerNames.length ? buyerNames.join(', ') : DASH },
    { label: 'Seller(s)', value: sellerNames.length ? sellerNames.join(', ') : DASH },
    { label: 'Acceptance Date', value: fmtDate(deal.contractAcceptanceDate) },
    { label: 'Escrow Number', value: fmt(deal.escrowNumber) },
    { label: 'Transaction Email', value: fmt(deal.transactionEmail) },
    { label: 'Reviewer', value: fmt(deal.reviewerName) },
    { label: 'Year Built', value: fmt(prop.yearBuilt) },
    { label: 'Type', value: fmt(deal.dealType || deal.checklistType) },
    { label: 'Checklist Type', value: fmt(deal.checklistTypeName || deal.checklistType) },
    { label: 'Office', value: fmt(deal.officeName || deal.office) },
  ];

  return (
    <div>
      {/* Header card */}
      <div style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
        padding: '20px', marginBottom: '20px',
      }}>
        <div style={{
          fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '14px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{
            background: dealType === 'listing' ? '#7c3aed' : '#1d4ed8',
            color: '#fff', padding: '3px 10px', borderRadius: '4px',
            fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {dealType === 'listing' ? 'Listing' : 'Sale'}
          </span>
          {address}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '5px 24px' }}>
          {headerFields.map(f => (
            <div key={f.label} style={{ display: 'flex', gap: '6px', fontSize: '13px', padding: '2px 0' }}>
              <span style={{ color: '#64748b', minWidth: '140px', flexShrink: 0 }}>{f.label}:</span>
              <span style={{ color: '#0f172a', fontWeight: '500' }}>{f.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 18px', background: 'none', border: 'none',
              borderBottom: activeTab === tab ? '2px solid #1d4ed8' : '2px solid transparent',
              marginBottom: '-2px', color: activeTab === tab ? '#1d4ed8' : '#6b7280',
              fontWeight: activeTab === tab ? '600' : '400', cursor: 'pointer', fontSize: '14px',
            }}
          >
            {tab}
            {tab === 'Checklist' && activities.length > 0 && (
              <span style={{
                marginLeft: '6px', background: '#e0e7ff', color: '#3730a3',
                borderRadius: '9999px', padding: '1px 6px', fontSize: '11px',
              }}>{activities.length}</span>
            )}
            {tab === 'Documents' && documents.length > 0 && (
              <span style={{
                marginLeft: '6px', background: '#dcfce7', color: '#15803d',
                borderRadius: '9999px', padding: '1px 6px', fontSize: '11px',
              }}>{documents.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'Summary' && <SummaryTab deal={deal} dealType={dealType} />}
      {activeTab === 'Contacts' && <ContactsTab deal={deal} dealType={dealType} />}
      {activeTab === 'Commission' && <CommissionTab deal={deal} />}
      {activeTab === 'Checklist' && <ChecklistTab activities={activities} listingActivities={listingActivities} />}
      {activeTab === 'Documents' && <DocumentsTab documents={documents} docAssignmentMap={docAssignmentMap} />}
    </div>
  );
}

// ── Deal List ─────────────────────────────────────────────────────────────────

function DealList({ title, items, selectedGuid, onSelect, color }) {
  if (items.length === 0) {
    return (
      <div>
        <div style={{ fontSize: '13px', fontWeight: '700', color, marginBottom: '8px' }}>{title} (0)</div>
        <p style={{ fontSize: '13px', color: '#9ca3af' }}>None found.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '13px', fontWeight: '700', color, marginBottom: '8px' }}>
        {title} ({items.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {items.map(item => {
          // Bulk file list returns flat propertyAddress field and a saleGuid/listingGuid.
          // _address is a backend-computed fallback built from the nested property object
          // when propertyAddress is absent (common for listing-type files).
          const guid = item.saleGuid || item.listingGuid || item.id || item.fileId;
          const addr = item._address || item.propertyAddress || item.address || guid || 'Unknown';
          const status = item.status || '';
          const price = item.salePrice || item.listingPrice || item.price;
          const isSelected = selectedGuid === guid;
          return (
            <button
              key={guid}
              onClick={() => onSelect(guid, item)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px',
                border: isSelected ? `2px solid ${color}` : '1px solid #e5e7eb',
                borderRadius: '6px', background: isSelected ? color + '0d' : '#fff',
                cursor: 'pointer', textAlign: 'left', gap: '8px',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px', fontWeight: '600', color: '#111827',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {addr}
                </div>
                {status && <div style={{ fontSize: '11px', color: '#6b7280' }}>{status}</div>}
              </div>
              {price != null && (
                <div style={{ flexShrink: 0, fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                  ${parseFloat(price).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function DeveloperSkySlope() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    return ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'].includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) navigate('/');
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const [users, setUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');

  // Raw lists — always fetched with no status filter (SkySlope's status= param is
  // unreliable: "incomplete" records from listing→sale conversions are not returned
  // when status=incomplete is passed, even though they appear with no filter).
  const [allListings, setAllListings] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active,pending,incomplete,expired');

  // Client-side filter using actual DTO status strings observed from SkySlope responses.
  // SkySlope DTO status strings differ from the API filter param values:
  //   DTO "Canceled/Pend"  ← API filter "canceledPending"
  //   DTO "Canceled/Appr"  ← API filter "canceledApproved"
  //   DTO "Incomplete"     ← API filter "incomplete"  (but server-side filter is broken for this)
  //   DTO "Active" / "Pending" / "Closed" / "Expired" / "Archived"  (likely Title-cased)
  const matchesFilter = useCallback((item) => {
    const s = (item.status || '').toLowerCase();
    if (statusFilter === 'all') return true;
    if (statusFilter === 'exclude_cancelled')
      return !s.startsWith('cancel') && s !== 'archived' && s !== 'expired';
    // All other filters are explicit inclusion lists of known DTO status values
    const allowed = new Set(statusFilter.toLowerCase().split(',').map(x => x.trim()));
    return allowed.has(s);
  }, [statusFilter]);

  const listings = useMemo(() => allListings.filter(matchesFilter), [allListings, matchesFilter]);
  const sales = useMemo(() => allSales.filter(matchesFilter), [allSales, matchesFilter]);

  const [selectedGuid, setSelectedGuid] = useState(null);
  const [selectedDealType, setSelectedDealType] = useState(null);
  const [dealData, setDealData] = useState(null);
  const [dealDocuments, setDealDocuments] = useState([]);
  const [dealLoading, setDealLoading] = useState(false);
  const [dealError, setDealError] = useState('');

  // Load users with SkySlope on mount
  useEffect(() => {
    if (!isAuthenticated || !isKeyFloUser) return;
    axios.get(`${BASE_URL}/skyslope/users`)
      .then(res => {
        const list = res.data.users || [];
        setUsers(list);
        if (list.length > 0) setSelectedUserEmail(list[0].email);
      })
      .catch(err => console.error('Failed to load SkySlope users', err));
  }, [isAuthenticated, isKeyFloUser]);

  const loadFiles = useCallback(() => {
    if (!selectedUserEmail) return;
    setFilesLoading(true);
    setFilesError('');
    setSelectedGuid(null);
    setDealData(null);
    setDealDocuments([]);
    // Always fetch all — no status param sent. SkySlope's server-side status filter
    // is unreliable (incomplete records from listing→sale conversions are silently
    // dropped when status=incomplete is passed). Filtering is applied client-side.
    axios.get(`${BASE_URL}/skyslope/files`, { params: { user_email: selectedUserEmail } })
      .then(res => {
        setAllListings(res.data.listings || []);
        setAllSales(res.data.sales || []);
      })
      .catch(err => setFilesError(err.response?.data?.error || err.message))
      .finally(() => setFilesLoading(false));
  }, [selectedUserEmail]);

  useEffect(() => {
    if (selectedUserEmail) loadFiles();
  }, [selectedUserEmail, loadFiles]);

  const handleSelectDeal = useCallback((guid, item) => {
    // objectType from the bulk export ('sale' | 'listing') is authoritative.
    // Do NOT use item.listingGuid — a sale converted from a listing has listingGuid
    // set but is still a sale and must load from the /sale/<guid> endpoint.
    const dealType = item.objectType === 'listing' ? 'listing' : 'sale';
    setSelectedGuid(guid);
    setSelectedDealType(dealType);
    setDealData(null);
    setDealDocuments([]);
    setDealError('');
    setDealLoading(true);

    const endpoint = dealType === 'listing'
      ? `${BASE_URL}/skyslope/listing/${guid}`
      : `${BASE_URL}/skyslope/sale/${guid}`;

    axios.get(endpoint, { params: { user_email: selectedUserEmail } })
      .then(res => {
        setDealData(res.data);
        setDealDocuments(res.data.documents || []);
      })
      .catch(err => setDealError(err.response?.data?.error || err.message))
      .finally(() => setDealLoading(false));
  }, [selectedUserEmail]);

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <button onClick={() => loginWithRedirect()}>Log In</button>
      </div>
    );
  }
  if (!isKeyFloUser) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Top bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
            SkySlope Viewer
          </h1>
        </div>
        <DeveloperNavigation />
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>

        {/* User + status selector */}
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
          padding: '16px 20px', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
        }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', flexShrink: 0 }}>User</label>
          <select
            value={selectedUserEmail}
            onChange={e => setSelectedUserEmail(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minWidth: '260px' }}
          >
            {users.length === 0 && <option value="">No SkySlope users found</option>}
            {users.map(u => (
              <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
            ))}
          </select>

          <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', flexShrink: 0, marginLeft: '8px' }}>
            Status Filter
          </label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
          >
            <option value="active,pending,incomplete,expired">Active + Pending + Incomplete + Expired</option>
            <option value="exclude_cancelled">Exclude Cancelled / Expired / Archived</option>
            <option value="all">All statuses</option>
            <option value="active,pending,incomplete">Active + Pending + Incomplete</option>
            <option value="active">Active only</option>
            <option value="pending">Pending only</option>
            <option value="closed">Closed only</option>
          </select>

          <button
            onClick={loadFiles}
            disabled={filesLoading || !selectedUserEmail}
            style={{
              padding: '8px 16px', background: '#1d4ed8', color: '#fff', border: 'none',
              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
              opacity: filesLoading || !selectedUserEmail ? 0.6 : 1,
            }}
          >
            {filesLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {filesError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px',
            padding: '12px 16px', color: '#dc2626', marginBottom: '16px', fontSize: '13px',
          }}>
            {filesError}
          </div>
        )}

        {/* Two-column layout: lists on left, detail on right */}
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '20px', alignItems: 'start' }}>

          {/* Left: file lists */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
              {filesLoading ? <LoadingSpinner /> : (
                <DealList
                  title="Listings"
                  items={listings}
                  selectedGuid={selectedGuid}
                  onSelect={(guid, item) => handleSelectDeal(guid, { ...item, objectType: 'listing' })}
                  color="#7c3aed"
                />
              )}
            </div>
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
              {filesLoading ? <LoadingSpinner /> : (
                <DealList
                  title="Sale Transactions"
                  items={sales}
                  selectedGuid={selectedGuid}
                  onSelect={(guid, item) => handleSelectDeal(guid, { ...item, objectType: 'sale' })}
                  color="#1d4ed8"
                />
              )}
            </div>
          </div>

          {/* Right: deal detail */}
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
            padding: '20px', minHeight: '300px',
          }}>
            {!selectedGuid && !dealLoading && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '200px', color: '#9ca3af', fontSize: '14px',
              }}>
                Select a listing or sale transaction to view details
              </div>
            )}
            {dealLoading && <LoadingSpinner />}
            {dealError && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px',
                padding: '12px 16px', color: '#dc2626', fontSize: '13px',
              }}>
                {dealError}
              </div>
            )}
            {dealData && !dealLoading && (
              <DealDetail
                dealData={dealData}
                dealType={selectedDealType}
                documents={dealDocuments}
              />
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default DeveloperSkySlope;
